/* Color plumbing for the ink canvas: oklch/hex → sRGB plus a cached soft-blob
 * sprite per color (a radialGradient per particle per frame is the #1 Canvas2D
 * perf killer — stamping a cached bitmap keeps hundreds of particles smooth). */

function oklchToRgb(L: number, C: number, H: number): [number, number, number] {
  const hr = (H * Math.PI) / 180;
  const a = C * Math.cos(hr);
  const b = C * Math.sin(hr);
  let l_ = L + 0.3963377774 * a + 0.2158037573 * b;
  let m_ = L - 0.1055613458 * a - 0.0638541728 * b;
  let s_ = L - 0.0894841775 * a - 1.291485548 * b;
  l_ = l_ ** 3;
  m_ = m_ ** 3;
  s_ = s_ ** 3;
  const r = 4.0767416621 * l_ - 3.3077115913 * m_ + 0.2309699292 * s_;
  const g = -1.2684380046 * l_ + 2.6097574011 * m_ - 0.3413193965 * s_;
  const bl = -0.0041960863 * l_ - 0.7034186147 * m_ + 1.707614701 * s_;
  const gam = (c: number) => {
    c = c <= 0.0031308 ? 12.92 * c : 1.055 * Math.pow(c, 1 / 2.4) - 0.055;
    return Math.max(0, Math.min(1, c));
  };
  return [Math.round(gam(r) * 255), Math.round(gam(g) * 255), Math.round(gam(bl) * 255)];
}

const colorCache = new Map<string, [number, number, number]>();

export function rgbOf(col: string | null | undefined): [number, number, number] {
  if (!col) return [180, 170, 160];
  const hit = colorCache.get(col);
  if (hit) return hit;
  let rgb: [number, number, number] = [180, 170, 160];
  const m = /oklch\(\s*([\d.]+)\s+([\d.]+)\s+([\d.]+)/i.exec(col);
  if (m) {
    rgb = oklchToRgb(+m[1]!, +m[2]!, +m[3]!);
  } else {
    const h = /^#?([0-9a-f]{6})$/i.exec(col.trim());
    if (h) {
      const n = parseInt(h[1]!, 16);
      rgb = [(n >> 16) & 255, (n >> 8) & 255, n & 255];
    }
  }
  colorCache.set(col, rgb);
  return rgb;
}

/** 128px tile of random gray — the banding-dither source. */
export function makeNoise(): HTMLCanvasElement {
  const D = 128;
  const cv = document.createElement("canvas");
  cv.width = cv.height = D;
  const g = cv.getContext("2d")!;
  const img = g.createImageData(D, D);
  for (let i = 0; i < img.data.length; i += 4) {
    const v = Math.floor(Math.random() * 256);
    img.data[i] = img.data[i + 1] = img.data[i + 2] = v;
    img.data[i + 3] = 255;
  }
  g.putImageData(img, 0, 0);
  return cv;
}

const spriteCache = new Map<string, HTMLCanvasElement>();

/** A 64px soft circle baked per color, ready for drawImage stamping. */
export function spriteOf(col: string): HTMLCanvasElement {
  const hit = spriteCache.get(col);
  if (hit) return hit;
  const D = 64;
  const cv = document.createElement("canvas");
  cv.width = cv.height = D;
  const g = cv.getContext("2d")!;
  const [r, gr, b] = rgbOf(col);
  // Gaussian-ish falloff: the old 3-stop piecewise-linear ramp had a slope
  // break at 0.55 that read as a ring once 8-bit alpha banding set in.
  const grad = g.createRadialGradient(D / 2, D / 2, 0, D / 2, D / 2, D / 2);
  grad.addColorStop(0, `rgba(${r},${gr},${b},1)`);
  grad.addColorStop(0.25, `rgba(${r},${gr},${b},0.85)`);
  grad.addColorStop(0.5, `rgba(${r},${gr},${b},0.55)`);
  grad.addColorStop(0.7, `rgba(${r},${gr},${b},0.3)`);
  grad.addColorStop(0.85, `rgba(${r},${gr},${b},0.12)`);
  grad.addColorStop(1, `rgba(${r},${gr},${b},0)`);
  g.fillStyle = grad;
  g.fillRect(0, 0, D, D);
  spriteCache.set(col, cv);
  return cv;
}
